import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./VistaPaywall.module.scss"

/**
 * Vista — a NON-ONBOARDING paywall built to sell the *Ultimate* tier to a user
 * who is already inside the app and already has a library. The active Slack
 * directive ("sell the ultimate subscription plan, default to ultimate but
 * allow buying premium as well") is the spine: Ultimate is the default choice,
 * Premium stays fully buyable, and yearly is the featured price for whichever
 * tier is selected.
 *
 * The mechanic — deliberately unlike anything else in the set (no lever, dial,
 * flick or mantel) — is a **horizon of your memories**. The screen is a
 * perspective field of photo tiles that recedes toward a glowing horizon, and
 * it lives in 3D: it parallaxes as you move the pointer, so you feel the depth
 * of the room behind your vault. Choosing a tier changes *how far the room
 * reaches*:
 *   • Ultimate (default) — a light-wave ignites the entire field to the
 *     horizon, the capacity readout counts 50 GB → 1 TB, and a second lock
 *     clasp closes. Room for everything: every photo & video, full quality.
 *   • Premium — only the near rows stay lit; the deep field honestly recedes to
 *     faint outline (room you haven't opened), capacity settles to 50 GB.
 * Nothing is ever deleted or "expires" — the tier only chooses how much room
 * you open. Value frame: abundance / future-self ("a whole year of memories
 * doesn't even dent a terabyte"), a register no prior paywall owns.
 *
 * Self-contained: renders on the built-in debug product data (Ultimate yearly
 * $119.99 / Premium yearly $35.99, both 7-day trial) and reuses the bundled
 * `nimbus/` memory photos — no native injection, no runtime fetches.
 */

type Tier = "ultimate" | "premium"

// Bundled memory tiles, repeated across the depth rows so the horizon is fully
// self-contained (no runtime fetches).
const MEMS = Array.from({ length: 16 }, (_, i) => `mem${i + 1}.jpg`)

// The field: rows recede from front (0) to horizon. Front rows are always lit;
// the deeper the row, the more "Ultimate-only" it is.
const ROWS = [
  { depth: 0, tiles: 6 },
  { depth: 1, tiles: 7 },
  { depth: 2, tiles: 8 },
  { depth: 3, tiles: 9 },
  { depth: 4, tiles: 10 },
]

const GB_PREMIUM = 50
const GB_ULTIMATE = 1000

const Glyph = ({
  name,
}: {
  name: "lock" | "search" | "arrow" | "close" | "check" | "layers" | "infinity"
}) => {
  switch (name) {
    case "lock":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect
            x="4.5"
            y="10.4"
            width="15"
            height="10.2"
            rx="3"
            fill="currentColor"
          />
          <path
            d="M8 10.4V7.6a4 4 0 0 1 8 0v2.8"
            stroke="currentColor"
            strokeWidth="2.1"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      )
    case "search":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle
            cx="11"
            cy="11"
            r="6.4"
            stroke="currentColor"
            strokeWidth="2.1"
          />
          <path
            d="m16 16 4.4 4.4"
            stroke="currentColor"
            strokeWidth="2.1"
            strokeLinecap="round"
          />
        </svg>
      )
    case "arrow":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M5 12h13m0 0-5-5m5 5-5 5"
            stroke="currentColor"
            strokeWidth="2.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "close":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="m6 6 12 12M18 6 6 18"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      )
    case "check":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="m5 12.5 4.5 4.5L19 7"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "layers":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 3.5 21 8l-9 4.5L3 8l9-4.5Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
            fill="none"
          />
          <path
            d="m4 12 8 4 8-4M4 16l8 4 8-4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "infinity":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M7.5 8.5c-2.2 0-3.5 1.6-3.5 3.5s1.3 3.5 3.5 3.5c2.9 0 4-3.5 9-3.5 2.2 0 3.5 1.6 3.5 3.5s-1.3 3.5-3.5 3.5c-2.9 0-4-7-9-7Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            transform="translate(0 -0.5)"
          />
        </svg>
      )
  }
}

const VistaPaywall = () => {
  const { state, actions } = useAppState()

  const ultimate = state.productData?.ultimate
  const premium = state.productData?.premium
  const locale =
    ultimate?.yearlyProduct?.priceLocale ?? premium?.yearlyProduct?.priceLocale

  const money = (v: number | undefined, fallback: string) =>
    v != null ? formatCurrency(v, locale) : fallback

  const plans = useMemo(
    () => ({
      ultimate: {
        yearly: ultimate?.yearlyProduct?.price ?? 119.99,
        monthlyPrice: ultimate?.monthlyProduct?.price ?? 19.99,
        product: ultimate?.yearlyProduct,
        monthlyProduct: ultimate?.monthlyProduct,
        hasTrial: ultimate?.yearlyProduct?.hasTrial ?? true,
        trialDays: ultimate?.yearlyProduct?.introPricing?.period ?? 7,
      },
      premium: {
        yearly: premium?.yearlyProduct?.price ?? 35.99,
        monthlyPrice: premium?.monthlyProduct?.price ?? 13.99,
        product: premium?.yearlyProduct,
        monthlyProduct: premium?.monthlyProduct,
        hasTrial: premium?.yearlyProduct?.hasTrial ?? true,
        trialDays: premium?.yearlyProduct?.introPricing?.period ?? 7,
      },
    }),
    [ultimate, premium],
  )

  // Default to Ultimate per the standing directive; Premium stays buyable.
  const [tier, setTier] = useState<Tier>("ultimate")
  const [billing, setBilling] = useState<"yearly" | "monthly">("yearly")
  const isUlt = tier === "ultimate"

  // Tint the OS inset bars to blend into the twilight horizon.
  useEffect(() => {
    actions.setHeaderInsetColor("#120a20")
    actions.setFooterInsetColor("#0f0a1a")
  }, [actions])

  // --- Animated capacity readout ---------------------------------------
  const [gb, setGb] = useState(GB_ULTIMATE)
  const gbRef = useRef(GB_ULTIMATE)
  const rafRef = useRef(0)
  useEffect(() => {
    const target = isUlt ? GB_ULTIMATE : GB_PREMIUM
    cancelAnimationFrame(rafRef.current)
    const from = gbRef.current
    const start = performance.now()
    const dur = 680
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur)
      const e = 1 - Math.pow(1 - t, 3) // easeOutCubic
      const val = Math.round(from + (target - from) * e)
      gbRef.current = val
      setGb(val)
      if (t < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [isUlt])

  const gbLabel =
    gb >= 1000
      ? `${(gb / 1000).toFixed(gb % 1000 === 0 ? 0 : 1)} TB`
      : `${gb} GB`

  // --- Pointer parallax on the depth field -----------------------------
  const stageRef = useRef<HTMLDivElement>(null)
  const parallaxRAF = useRef(0)
  const onStageMove = useCallback((e: React.PointerEvent) => {
    const el = stageRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const nx = (e.clientX - r.left) / r.width - 0.5 // -0.5 .. 0.5
    const ny = (e.clientY - r.top) / r.height - 0.5
    cancelAnimationFrame(parallaxRAF.current)
    parallaxRAF.current = requestAnimationFrame(() => {
      el.style.setProperty("--px", nx.toFixed(3))
      el.style.setProperty("--py", ny.toFixed(3))
    })
  }, [])
  const resetParallax = useCallback(() => {
    const el = stageRef.current
    if (!el) return
    el.style.setProperty("--px", "0")
    el.style.setProperty("--py", "0")
  }, [])

  // --- Pricing derivations ---------------------------------------------
  const active = plans[tier]
  const yearlyPrice = money(active.yearly, isUlt ? "$119.99" : "$35.99")
  const monthlyOfYear = money(active.yearly / 12, isUlt ? "$9.99" : "$2.99")
  const monthlyPrice = money(active.monthlyPrice, isUlt ? "$19.99" : "$13.99")
  const savePercent = Math.max(
    0,
    Math.round((1 - active.yearly / (active.monthlyPrice * 12)) * 100),
  )

  const selectedProduct =
    billing === "yearly" ? active.product : active.monthlyProduct
  const selectedPriceLine =
    billing === "yearly"
      ? active.hasTrial
        ? `${active.trialDays} days free, then ${yearlyPrice}/year`
        : `${yearlyPrice}/year`
      : `${monthlyPrice}/month`
  const ctaLabel =
    billing === "yearly" && active.hasTrial
      ? `Start ${active.trialDays} days free`
      : isUlt
        ? "Get Ultimate"
        : "Get Premium"

  const buy = useCallback(() => {
    if (selectedProduct) native.subscribe(selectedProduct)
  }, [selectedProduct])
  const close = useCallback(() => native.postNativeEvent("close"), [])

  return (
    <div className={styles.page} data-tier={tier}>
      <div className={styles.skyGlow} aria-hidden="true" />
      <div className={styles.grain} aria-hidden="true" />

      <button className={styles.close} onClick={close} aria-label="Not now">
        <Glyph name="close" />
      </button>

      {/* ---- The horizon of your memories ---- */}
      <section
        className={styles.stage}
        ref={stageRef}
        onPointerMove={onStageMove}
        onPointerLeave={resetParallax}
      >
        <div className={styles.field}>
          <div className={styles.horizonLine} aria-hidden="true" />
          {/* Rendered far → near so deeper rows layer behind the front. */}
          {[...ROWS].reverse().map((row) => (
            <div
              className={styles.row}
              key={row.depth}
              data-depth={row.depth}
              style={{ "--d": row.depth } as React.CSSProperties}
            >
              {Array.from({ length: row.tiles }).map((_, i) => {
                const mem = MEMS[(row.depth * 5 + i) % MEMS.length]
                return (
                  <span
                    className={styles.tile}
                    key={i}
                    style={{ "--i": i } as React.CSSProperties}
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/nimbus/${mem}`}
                      alt=""
                      draggable={false}
                      loading="eager"
                    />
                  </span>
                )
              })}
            </div>
          ))}
          <div className={styles.skyFade} aria-hidden="true" />
          <div className={styles.floorFade} aria-hidden="true" />
        </div>

        {/* Capacity readout floating over the horizon */}
        <div className={styles.readout}>
          <span className={styles.readNum}>{gbLabel}</span>
          <span className={styles.readWord}>
            <Glyph name="lock" />
            private storage
          </span>
        </div>

        <div className={styles.locks} aria-hidden="true">
          <span className={styles.lockChip}>
            <Glyph name="lock" />
          </span>
          <span className={`${styles.lockChip} ${styles.lockSecond}`}>
            <Glyph name="lock" />
          </span>
        </div>
      </section>

      {/* ---- Copy ---- */}
      <header className={styles.intro}>
        <span className={styles.kicker}>Private Photo Vault</span>
        <h1 className={styles.headline}>
          You&rsquo;ve kept the best.
          <br />
          <span className={styles.headlineAccent}>Now keep it all.</span>
        </h1>
        <p className={styles.sub}>
          {isUlt
            ? "Ultimate opens your vault to a full terabyte — room for every photo and video, in full quality, for years to come."
            : "Premium keeps your essentials safe in 50 GB of private space."}
        </p>
      </header>

      {/* ---- Tier choice: Ultimate default, Premium buyable ---- */}
      <div
        className={styles.tiers}
        role="tablist"
        aria-label="Choose your plan"
      >
        <button
          role="tab"
          aria-selected={isUlt}
          className={`${styles.tierChip} ${isUlt ? styles.tierOn : ""}`}
          onClick={() => setTier("ultimate")}
        >
          <span className={styles.tierName}>
            <Glyph name="infinity" />
            Ultimate
          </span>
          <span className={styles.tierMeta}>Room for everything</span>
        </button>
        <button
          role="tab"
          aria-selected={!isUlt}
          className={`${styles.tierChip} ${!isUlt ? styles.tierOn : ""}`}
          onClick={() => setTier("premium")}
        >
          <span className={styles.tierName}>Premium</span>
          <span className={styles.tierMeta}>The essentials</span>
        </button>
      </div>

      {/* ---- Features that light with the tier ---- */}
      <div className={styles.powers}>
        <div className={styles.power}>
          <span className={styles.powerIcon}>
            <Glyph name="layers" />
          </span>
          <span className={styles.powerText}>
            <span className={styles.powerHead}>
              Room
              <span className={styles.powerVal}>
                {isUlt ? "1 TB" : "50 GB"}
              </span>
            </span>
            <span className={styles.powerSub}>
              {isUlt
                ? "≈ 250,000 photos in full quality"
                : "For the photos that matter most"}
            </span>
          </span>
        </div>
        <div className={`${styles.power} ${isUlt ? "" : styles.powerOff}`}>
          <span className={styles.powerIcon}>
            <Glyph name="search" />
          </span>
          <span className={styles.powerText}>
            <span className={styles.powerHead}>
              Order
              {!isUlt && <span className={styles.ultBadge}>Ultimate</span>}
            </span>
            <span className={styles.powerSub}>
              Search, subalbums &amp; duplicate cleanup
            </span>
          </span>
        </div>
        <div className={`${styles.power} ${isUlt ? "" : styles.powerOff}`}>
          <span className={styles.powerIcon}>
            <Glyph name="lock" />
          </span>
          <span className={styles.powerText}>
            <span className={styles.powerHead}>
              Seal
              {!isUlt && <span className={styles.ultBadge}>Ultimate</span>}
            </span>
            <span className={styles.powerSub}>
              Dual lock &amp; alphanumeric passcode
            </span>
          </span>
        </div>
      </div>

      {/* ---- Pricing: yearly featured for the selected tier ---- */}
      <section className={styles.pricing}>
        <button
          className={`${styles.yearCard} ${
            billing === "yearly" ? styles.yearActive : ""
          }`}
          onClick={() => setBilling("yearly")}
          aria-pressed={billing === "yearly"}
        >
          <span className={styles.yearTop}>
            <span className={styles.yearName}>
              {isUlt ? "Ultimate" : "Premium"} · Yearly
            </span>
            <span className={styles.saveBadge}>SAVE {savePercent}%</span>
          </span>
          <span className={styles.yearMid}>
            <span className={styles.yearPerMo}>{monthlyOfYear}</span>
            <span className={styles.yearPerMoUnit}>/mo</span>
            <span className={styles.yearBilled}>
              {yearlyPrice} billed yearly
            </span>
          </span>
          <span className={styles.yearTrial}>
            {active.hasTrial
              ? `${active.trialDays}-day free trial included`
              : "Cancel anytime"}
          </span>
          <span className={styles.radio} aria-hidden="true" />
        </button>

        <button
          className={`${styles.monthLine} ${
            billing === "monthly" ? styles.monthActive : ""
          }`}
          onClick={() => setBilling("monthly")}
          aria-pressed={billing === "monthly"}
        >
          Prefer monthly? <strong>{monthlyPrice}/mo</strong>
        </button>

        <button className={styles.cta} onClick={buy}>
          <span>{ctaLabel}</span>
          <Glyph name="arrow" />
        </button>

        <p className={styles.fine}>
          {selectedPriceLine}. Auto-renews until canceled. Cancel anytime.
        </p>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </section>
    </div>
  )
}

export default VistaPaywall
